/**
 * TenantsApi.ts — Façade API du domaine tenants (Phase D2 Round 2).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 * Aucun import React ni Capacitor. Seul couplage externe autorisé :
 *   @/integrations/supabase/client et @/integrations/supabase/types.
 *
 * Invariants respectés :
 *   4.A.10 — tenant_id jamais dans le body ; RLS auth.uid() côté serveur.
 *   4.A.15 — aucun champ PII loggé (contact_email, email, address, siret, iban…).
 *   4.B.2  — zéro import supabase dans src/components/ ou src/pages/.
 *   4.B.4  — 0 any, types stricts dérivés de Database['public']['Tables'].
 *
 * Design : aucune RPC SECURITY DEFINER dédiée à ces opérations n'a été
 * identifiée dans pg_proc pour les tables tenants/tenant_modules/reseller_branding.
 * On utilise donc from() direct sur les tables RLS-protégées conformément au
 * pattern ADR 0001 (conditions RPC non requises — RLS seule suffit pour ces tables).
 *
 * RLS findings (staging tlimqhvplmxkprnpgqnf) :
 *   tenants         : SELECT → utilisateurs dont get_my_tenant_id() = id
 *                     UPDATE → admin/super_admin du tenant uniquement
 *   tenant_modules  : SELECT → membres du tenant (isOneToOne avec tenants)
 *                     UPDATE → admin/super_admin uniquement
 *   reseller_branding : SELECT → membres du tenant revendeur
 *                       INSERT/UPDATE → admin/super_admin du tenant revendeur
 *
 * PII RGPD : tenants.contact_email, email, phone, address, siret, iban,
 *   accountant_email, tva_number, bic_swift, stripe_customer_id sont PII/sensibles.
 *   Ne JAMAIS logger ces champs. Invariant 4.A.15.
 *
 * @see docs/adr/0001-finalize-fiscal-sale-rpc-client-callable.md
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err, errOf } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, notFoundError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import type {
  Tenant,
  TenantModules,
  ModuleSlug,
  ResellerBranding,
  ResellerBrandingUpdate,
  ResellerBrandingInsert,
  TenantSettings,
  TenantSettingsUpdate,
  TenantAccountantUpdate,
  TenantAdminUpdate,
  AccountantEmailSend,
  AccountantEmailSendInsert,
  PurgeOrphanTenantResult,
} from './types';
import type { Database } from '@/integrations/supabase/types';

// ---------------------------------------------------------------------------
// Types internes (non-exportés)
// ---------------------------------------------------------------------------

type TenantRow = Database['public']['Tables']['tenants']['Row'];
type TenantModulesRow = Database['public']['Tables']['tenant_modules']['Row'];
type ResellerBrandingRow = Database['public']['Tables']['reseller_branding']['Row'];
type AccountantEmailSendRow = Database['public']['Tables']['accountant_email_sends']['Row'];

// ---------------------------------------------------------------------------
// TenantsApi
// ---------------------------------------------------------------------------

export const TenantsApi = {
  /**
   * Retourne la ligne complète du tenant accessible au caller via RLS.
   *
   * RLS garantit que seul le tenant du caller est retourné.
   * En cas d'absence de ligne (tenant non provisionné), retourne not_found.
   *
   * PII : nombreux champs (contact_email, email, siret, iban, etc.).
   * Ne jamais logger le retour. Invariant 4.A.15.
   */
  async getMyTenant(): Promise<Result<Tenant, AppError>> {
    const { data, error } = await supabase.from('tenants').select('*').single();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.getMyTenant'));
    }

    if (!data) {
      return err(notFoundError('tenant', 'current_user'));
    }

    return ok(data as TenantRow);
  },

  /**
   * Retourne la ligne tenant_modules du tenant courant.
   *
   * La table tenant_modules a une relation isOneToOne avec tenants.
   * RLS garantit la visibilité uniquement pour les membres du tenant.
   *
   * Retourne null si aucune ligne (tenant sans configuration modules).
   */
  async listMyModules(): Promise<Result<TenantModules | null, AppError>> {
    const { data, error } = await supabase.from('tenant_modules').select('*').maybeSingle();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.listMyModules'));
    }

    return ok((data as TenantModulesRow | null) ?? null);
  },

  /**
   * Vérifie si un module donné est activé pour le tenant courant.
   *
   * Retourne false si aucune configuration modules n'existe pour ce tenant.
   * Type-safe : ModuleSlug ne contient que les clés mod_* valides.
   *
   * @param slug  Slug du module (ex: 'mod_cuisine_kds', 'mod_stocks').
   */
  async isModuleEnabled(slug: ModuleSlug): Promise<Result<boolean, AppError>> {
    const modulesResult = await TenantsApi.listMyModules();

    if (!modulesResult.ok) {
      return err(errOf(modulesResult));
    }

    if (modulesResult.value === null) {
      return ok(false);
    }

    const value = modulesResult.value[slug];

    return ok(value === true);
  },

  /**
   * Retourne le branding revendeur du tenant courant.
   *
   * La table reseller_branding est liée à tenants via reseller_tenant_id.
   * RLS garantit la visibilité uniquement pour les membres du tenant revendeur.
   *
   * Retourne null si aucun branding configuré (cas nominal pour tenant marchand
   * non-revendeur).
   *
   * PII potentielle : support_email, support_phone.
   * Ne jamais logger le retour. Invariant 4.A.15.
   */
  async getMyBranding(): Promise<Result<ResellerBranding | null, AppError>> {
    const { data, error } = await supabase.from('reseller_branding').select('*').maybeSingle();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.getMyBranding'));
    }

    return ok((data as ResellerBrandingRow | null) ?? null);
  },

  /**
   * Retourne les settings de configuration du tenant courant (sous-ensemble non-PII).
   *
   * Retourne uniquement les champs de TenantSettings — les champs PII/financiers
   * (email, siret, iban, etc.) sont exclus de ce type par construction.
   * Pour accéder aux champs complets, utiliser getMyTenant().
   *
   * RLS garantit que seul le tenant du caller est retourné.
   */
  async getMySettings(): Promise<Result<TenantSettings, AppError>> {
    const { data, error } = await supabase
      .from('tenants')
      .select(
        [
          'id',
          'name',
          'slug',
          'business_type',
          'logo_url',
          'loyalty_points_value',
          'force_mfa',
          'revenue_mask_enabled',
          'revenue_mask_code',
          'sms_module_enabled',
          'sms_credits_monthly',
          'sms_credits_used',
          'sms_credits_reset_at',
          'auto_send_fec',
          'auto_send_fec_daily',
          'auto_send_fec_weekly',
          'auto_send_fec_yearly',
          'subscription_status',
          'subscription_plan',
          'status',
          'is_deployed',
          'is_demo',
          'google_review_url',
          'google_place_id',
          'updated_at',
          'created_at',
        ].join(','),
      )
      .single<TenantSettings>();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.getMySettings'));
    }

    if (!data) {
      return err(notFoundError('tenant_settings', 'current_user'));
    }

    return ok(data);
  },

  /**
   * Met à jour le branding revendeur du tenant courant.
   *
   * Admin only — RLS côté serveur refuse toute mise à jour sans rôle admin.
   * Si aucune ligne branding n'existe, tente un upsert via insert.
   *
   * PII potentielle : support_email, support_phone.
   * Ne jamais logger les arguments. Invariant 4.A.15.
   *
   * @param updates  Champs à mettre à jour.
   */
  async updateMyBranding(
    updates: ResellerBrandingUpdate,
  ): Promise<Result<ResellerBranding, AppError>> {
    // Même garde que updateMySettings : supabase-js refuse un UPDATE sans
    // filtre. On résout le tenant revendeur courant via get_my_tenant_id()
    // (invariant 4.A.10) et on cible sa ligne de branding.
    const { data: tenantId, error: tenantErr } = await supabase.rpc('get_my_tenant_id');
    if (tenantErr) {
      return err(appErrorFromSupabase(tenantErr, 'TenantsApi.updateMyBranding'));
    }
    if (!tenantId) {
      return err(notFoundError('reseller_branding', 'current_tenant'));
    }

    const { data, error } = await supabase
      .from('reseller_branding')
      .update(updates)
      .eq('reseller_tenant_id', tenantId)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.updateMyBranding'));
    }

    if (!data) {
      return err(notFoundError('reseller_branding', 'current_tenant'));
    }

    return ok(data as ResellerBrandingRow);
  },

  /**
   * Crée ou remplace le branding revendeur du tenant courant.
   *
   * Admin only — RLS côté serveur refuse toute insertion sans rôle admin.
   * Utiliser quand aucun branding n'existe encore (upsert via insert).
   *
   * PII potentielle : support_email, support_phone.
   * Ne jamais logger les arguments. Invariant 4.A.15.
   *
   * @param branding  Données branding à créer (reseller_tenant_id requis).
   */
  async upsertMyBranding(
    branding: ResellerBrandingInsert,
  ): Promise<Result<ResellerBranding, AppError>> {
    const { data, error } = await supabase
      .from('reseller_branding')
      .upsert(branding, { onConflict: 'reseller_tenant_id' })
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.upsertMyBranding'));
    }

    if (!data) {
      return err(notFoundError('reseller_branding', 'current_tenant'));
    }

    return ok(data as ResellerBrandingRow);
  },

  /**
   * Met à jour les settings de configuration du tenant courant, incluant
   * les champs comptables (accountant_*) et les flags d'auto-envoi FEC.
   *
   * Admin only — RLS côté serveur refuse toute mise à jour sans rôle admin.
   * Les champs TenantSettingsUpdate (non-PII) et TenantAccountantUpdate (PII comptable)
   * sont fusionnés dans un seul UPDATE pour couvrir les call sites :
   *   ClotureComptable.tsx:188 — saveEmailRecipients()
   *   ClotureComptable.tsx:217 — handleToggleAuto()
   *
   * Invariant 4.A.10 : tenant_id absent du body — RLS auth.uid() côté serveur.
   * Invariant 4.A.15 : accountant_email, accountant_cc_emails, accountant_default_note
   *   sont PII business — ne jamais logger les arguments ni le retour.
   *
   * @param updates  Champs de configuration et/ou comptables à mettre à jour.
   */
  async updateMySettings(
    updates: TenantSettingsUpdate & TenantAccountantUpdate,
  ): Promise<Result<TenantSettings, AppError>> {
    // supabase-js refuse tout UPDATE sans filtre ("UPDATE requires a WHERE
    // clause"). On résout le tenant courant via get_my_tenant_id() — id du
    // serveur, jamais du body (invariant 4.A.10) — et on cible la ligne.
    // La RLS WITH CHECK reste le garde-fou réel d'isolation.
    const { data: tenantId, error: tenantErr } = await supabase.rpc('get_my_tenant_id');
    if (tenantErr) {
      return err(appErrorFromSupabase(tenantErr, 'TenantsApi.updateMySettings'));
    }
    if (!tenantId) {
      return err(notFoundError('tenant_settings', 'current_user'));
    }

    const { data, error } = await supabase
      .from('tenants')
      .update(updates)
      .eq('id', tenantId)
      .select(
        [
          'id',
          'name',
          'slug',
          'business_type',
          'logo_url',
          'loyalty_points_value',
          'force_mfa',
          'revenue_mask_enabled',
          'revenue_mask_code',
          'sms_module_enabled',
          'sms_credits_monthly',
          'sms_credits_used',
          'sms_credits_reset_at',
          'auto_send_fec',
          'auto_send_fec_daily',
          'auto_send_fec_weekly',
          'auto_send_fec_yearly',
          'subscription_status',
          'subscription_plan',
          'status',
          'is_deployed',
          'is_demo',
          'google_review_url',
          'google_place_id',
          'updated_at',
          'created_at',
        ].join(','),
      )
      .single<TenantSettings>();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.updateMySettings'));
    }

    if (!data) {
      return err(notFoundError('tenant_settings', 'current_user'));
    }

    return ok(data);
  },

  // ---------------------------------------------------------------------------
  // Méthodes admin cross-tenant (Phase D4c)
  // ---------------------------------------------------------------------------

  /**
   * Liste tous les tenants — réservé aux super_admin.
   *
   * Appelle l'EF admin-users action=list_tenants (utilise service_role côté serveur
   * pour bypasser RLS). L'EF vérifie le rôle super_admin avant d'exécuter.
   *
   * Call sites : AuditLogsPanel.tsx:1018, AdminPortal.tsx:500.
   *
   * PII : les tenants contiennent des champs PII (email, siret, iban, etc.).
   * Ne jamais logger la liste retournée. Invariant 4.A.15.
   *
   * Invariant 4.A.17 : réservé super_admin, vérifié côté EF.
   */
  /**
   * Un tenant par id — contextes admin (panneaux cross-tenant) et revendeur.
   * Lecture directe : RLS décide (super_admin voit tout, un tenant ne voit
   * que lui-même). Retourne null si invisible/inexistant.
   *
   * PII : Row complet (email, siret…) — ne jamais logger. Invariant 4.A.15.
   */
  async getTenantById(id: string): Promise<Result<TenantRow | null, AppError>> {
    const { data, error } = await supabase.from('tenants').select('*').eq('id', id).maybeSingle();
    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.getTenantById'));
    }
    return ok(data);
  },

  async listTenants(): Promise<Result<TenantRow[], AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'list_tenants' },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.listTenants'));
    }

    const tenants = (data as { tenants?: TenantRow[] } | null)?.tenants;

    if (!tenants) {
      return ok([]);
    }

    return ok(tenants);
  },

  /**
   * Met à jour un tenant quelconque — réservé aux super_admin.
   *
   * Appelle l'EF admin-users action=update_tenant (service_role côté serveur).
   * L'allowlist des champs est définie dans l'EF (L261-305 de admin-users/index.ts).
   *
   * Call site : CreateClientWizard.tsx:246.
   *
   * PII : updates peut contenir siret, iban, contact_email, etc.
   * Ne jamais logger les arguments. Invariant 4.A.15.
   *
   * Invariant 4.A.17 : réservé super_admin, vérifié côté EF.
   * Invariant 4.A.10 : tenant_id passé en argument nommé distinct, pas dans
   *   le body RLS-sensible (l'EF l'extrait du body explicitement comme identifiant cible).
   *
   * @param tenantId  UUID du tenant à mettre à jour.
   * @param updates   Champs à mettre à jour (allowlist dans TenantAdminUpdate).
   */
  async updateTenant(
    tenantId: string,
    updates: TenantAdminUpdate,
  ): Promise<Result<void, AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'update_tenant', tenant_id: tenantId, ...updates },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.updateTenant'));
    }

    const d = data as { error?: string; success?: boolean } | null;

    if (d?.error) {
      return err({ type: 'supabase', operation: 'TenantsApi.updateTenant', message: d.error });
    }

    return ok(undefined);
  },

  /**
   * Purge un tenant orphelin via soft-delete — réservé aux super_admin.
   *
   * Appelle la RPC purge_orphan_tenant (SECURITY DEFINER côté serveur).
   * Soft-delete : renomme le tenant [PURGED], préserve toutes les ventes NF525
   * (invariant 4.A.8 — pas de DDL destructive sur tables fiscales).
   *
   * Call site : AdminPortal.tsx:2766.
   *
   * Invariant 4.A.17 : réservé super_admin, vérifié via has_role côté RPC
   * (wrapper → purge_tenant_safe, rétro-importé 20260602214108).
   *
   * @param tenantId  UUID du tenant orphelin à purger.
   */
  async purgeOrphanTenant(tenantId: string): Promise<Result<PurgeOrphanTenantResult, AppError>> {
    const { data, error } = await supabase.rpc('purge_orphan_tenant', {
      p_tenant_id: tenantId,
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.purgeOrphanTenant'));
    }

    const d = data as PurgeOrphanTenantResult | null;

    if (!d) {
      return err(notFoundError('tenant', tenantId));
    }

    if (d.error) {
      return err({ type: 'supabase', operation: 'TenantsApi.purgeOrphanTenant', message: d.error });
    }

    return ok(d);
  },

  /**
   * Enregistre un envoi comptable dans la table accountant_email_sends.
   *
   * Cette méthode encapsule l'INSERT dans accountant_email_sends pour les call
   * sites qui logguent manuellement un envoi (distinct du flow principal via l'EF
   * accountant-send qui écrit lui-même).
   *
   * Call site potentiel : ClotureComptable.tsx:161 (query accountant_sends).
   * La table est déjà écrite par l'EF accountant-send ; cette méthode couvre
   * les cas d'insertion directe côté client si nécessaire.
   *
   * PII : recipient_primary est un email (PII). Ne jamais logger les arguments.
   * Invariant 4.A.15.
   *
   * @param send  Données d'envoi à enregistrer.
   */
  async logAccountantEmailSend(
    send: AccountantEmailSendInsert,
  ): Promise<Result<AccountantEmailSend, AppError>> {
    const { data, error } = await supabase
      .from('accountant_email_sends')
      .insert(send)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.logAccountantEmailSend'));
    }

    if (!data) {
      return err(notFoundError('accountant_email_send', 'inserted'));
    }

    return ok(data as AccountantEmailSendRow);
  },

  /**
   * Liste les envois comptables du tenant courant.
   *
   * RLS garantit que seuls les envois du tenant du caller sont retournés.
   * La table accountant_email_sends est filtrée par tenant_id via RLS.
   *
   * Call site : ClotureComptable.tsx:161 — query accountant_sends.
   *
   * PII : recipient_primary et recipient_cc contiennent des emails.
   * Ne jamais logger le retour. Invariant 4.A.15.
   *
   * @param limit  Nombre max de lignes (défaut : 30, cohérent avec le call site).
   */
  async listAccountantEmailSends(limit = 30): Promise<Result<AccountantEmailSend[], AppError>> {
    const { data, error } = await supabase
      .from('accountant_email_sends')
      .select('*')
      .order('sent_at', { ascending: false })
      .limit(limit);

    if (error) {
      return err(appErrorFromSupabase(error, 'TenantsApi.listAccountantEmailSends'));
    }

    return ok((data ?? []) as AccountantEmailSendRow[]);
  },
} as const;
